import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { PageHeader, Empty, Modal, Chip } from "../components/ui-bits";
import {
  Plus, Trash2, Edit3, Search, Sparkles, ExternalLink, MapPin, Mail, Phone,
  ShieldCheck, RefreshCw, Loader2, FileText, Info, X,
} from "lucide-react";
import { useAutoOpenFromQuery } from "../lib/useAutoOpenFromQuery";

const CATEGORIES = ["Tessuti", "Accessori", "Packaging", "Stampe/Ricami",
                    "Lavanderia/Trattamenti", "Produzione/Confezione", "Logistica", "Altro"];
const STATUSES = ["Da valutare", "Approvato", "Preferito", "Sospeso"];
const CONFIDENCE_LABELS = {
  verified_official: { label: "Sito ufficiale verificato", tone: "green" },
  multi_source: { label: "Confermato da più fonti", tone: "blue" },
  partial: { label: "Informazioni parziali", tone: "gold" },
  unverified: { label: "Da verificare", tone: "red" },
};
const STATUS_STYLE = {
  "Da valutare": { bg: "rgba(115,115,115,.16)", fg: "var(--text-primary)" },
  "Approvato":   { bg: "rgba(15,133,84,.16)",   fg: "#0F8554" },
  "Preferito":   { bg: "rgba(212,175,55,.18)",  fg: "#8A6C0A" },
  "Sospeso":     { bg: "rgba(217,119,6,.18)",   fg: "#A45A05" },
};
function StatusChip({ value }) {
  const s = STATUS_STYLE[value] || STATUS_STYLE["Da valutare"];
  return (
    <span className="chip" style={{ background: s.bg, color: s.fg, borderColor: s.bg }}>
      <span className="w-1.5 h-1.5 rounded-full inline-block mr-1.5" style={{ background: s.fg }}/>
      {value || "—"}
    </span>
  );
}

const empty = {
  name: "", contact: "", email: "", phone: "", website: "",
  country: "", city: "", category: "", products_services: "",
  moq_value: "", moq_unit: "", lead_time_days: "", status: "Da valutare",
  notes: "",
};

export default function SuppliersPage() {
  const [tab, setTab] = useState("mine");
  return (
    <div data-testid="suppliers-page">
      <PageHeader eyebrow="Vendors" title="Fornitori"/>
      <div className="flex gap-1 border border-[color:var(--border,#E5E5E5)] rounded-none mb-6 w-fit">
        <button
          data-testid="suppliers-tab-mine"
          onClick={()=>setTab("mine")}
          className={`px-5 py-2 text-xs uppercase tracking-widest transition-colors ${tab==="mine"?"bg-[color:var(--text-primary,#0A0A0A)] text-[color:var(--background,#fff)]":"text-[color:var(--text-secondary,#525252)] hover:text-[color:var(--text-primary,#0A0A0A)]"}`}>
          I miei fornitori
        </button>
        <button
          data-testid="suppliers-tab-ai"
          onClick={()=>setTab("ai")}
          className={`px-5 py-2 text-xs uppercase tracking-widest transition-colors inline-flex items-center gap-2 ${tab==="ai"?"bg-[color:var(--text-primary,#0A0A0A)] text-[color:var(--background,#fff)]":"text-[color:var(--text-secondary,#525252)] hover:text-[color:var(--text-primary,#0A0A0A)]"}`}>
          <Sparkles size={12}/> Ricerca fornitori AI
        </button>
      </div>
      {tab === "mine" ? <MyVendorsTab/> : <AiSearchTab/>}
    </div>
  );
}

// -------------------------------------------------------- My Suppliers tab
function MyVendorsTab() {
  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ category: "", country: "", origin: "", status: "" });
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState(null);

  const load = () => {
    const params = { search: search || undefined };
    for (const k of ["category","country","origin","status"]) if (filters[k]) params[k] = filters[k];
    return api.get("/suppliers", { params }).then(r => setList(r.data));
  };
  useEffect(() => { load(); }, [search, filters]);

  const open = (r) => {
    setError("");
    if (r) { setForm({ ...empty, ...r }); setEditId(r.id); }
    else { setForm(empty); setEditId(null); }
    setModal(true);
  };
  useAutoOpenFromQuery(list, open);
  const save = async (e) => {
    e.preventDefault(); setError("");
    try {
      const payload = {
        ...form,
        moq_value: form.moq_value === "" || form.moq_value == null ? null : Number(form.moq_value),
        lead_time_days: form.lead_time_days === "" || form.lead_time_days == null ? null : Number(form.lead_time_days),
      };
      if (editId) await api.put(`/suppliers/${editId}`, payload);
      else await api.post("/suppliers", payload);
      setModal(false); load();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const remove = async (id) => { if (!window.confirm("Eliminare?")) return; await api.delete(`/suppliers/${id}`); load(); };

  // country dropdown values sourced from what's already saved (keeps the list small + relevant)
  const countryOptions = Array.from(new Set(list.map(s => s.country).filter(Boolean))).sort();

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-muted,#737373)]"/>
          <input data-testid="suppliers-search" placeholder="Cerca..." value={search} onChange={(e)=>setSearch(e.target.value)} className="input-dark pl-9"/>
        </div>
        <select data-testid="suppliers-filter-category" className="input-dark max-w-xs" value={filters.category} onChange={(e)=>setFilters(f=>({...f, category: e.target.value}))}>
          <option value="">Tutte le categorie</option>
          {CATEGORIES.map(c => <option key={c}>{c}</option>)}
        </select>
        <select data-testid="suppliers-filter-country" className="input-dark max-w-xs" value={filters.country} onChange={(e)=>setFilters(f=>({...f, country: e.target.value}))}>
          <option value="">Tutti i Paesi</option>
          {countryOptions.map(c => <option key={c}>{c}</option>)}
        </select>
        <select data-testid="suppliers-filter-origin" className="input-dark max-w-xs" value={filters.origin} onChange={(e)=>setFilters(f=>({...f, origin: e.target.value}))}>
          <option value="">Tutte le origini</option>
          <option value="Manuale">Manuale</option>
          <option value="AI">AI</option>
        </select>
        <select data-testid="suppliers-filter-status" className="input-dark max-w-xs" value={filters.status} onChange={(e)=>setFilters(f=>({...f, status: e.target.value}))}>
          <option value="">Tutti gli stati</option>
          {STATUSES.map(s => <option key={s}>{s}</option>)}
        </select>
        <button data-testid="new-supplier-btn" onClick={()=>open(null)} className="btn-gold inline-flex items-center gap-2 ml-auto">
          <Plus size={14}/>Nuovo fornitore
        </button>
      </div>

      {list.length === 0 ? <Empty title="Nessun fornitore presente" cta={{ label: "Aggiungi il primo fornitore", onClick: ()=>open(null) }} ctaTestId="suppliers-empty-cta"/> : (
        <div className="card-dark overflow-x-auto">
          <table className="tbl" data-testid="suppliers-table">
            <thead><tr>
              <th>Nome</th><th>Contatto</th><th>Categoria</th><th>Località</th>
              <th>MOQ</th><th>Lead time</th><th>Origine</th><th>Stato</th><th></th>
            </tr></thead>
            <tbody>
              {list.map(s => {
                const origin = s.discovered_via === "ai_search" ? "AI" : "Manuale";
                return (
                  <tr key={s.id} data-testid={`supplier-row-${s.id}`}>
                    <td><button className="hover:underline text-left" onClick={()=>setDetail(s)}>{s.name}</button></td>
                    <td className="text-[color:var(--text-secondary,#525252)]">{s.contact || s.email || "—"}</td>
                    <td>{s.category || "—"}</td>
                    <td>{[s.city, s.country].filter(Boolean).join(", ") || "—"}</td>
                    <td className="text-xs">{s.moq_value != null ? `${s.moq_value}${s.moq_unit?" "+s.moq_unit:""}` : "—"}</td>
                    <td className="text-xs">{s.lead_time_days != null ? `${s.lead_time_days} gg` : "—"}</td>
                    <td>
                      {origin === "AI"
                        ? <Chip tone="blue" data-testid={`supplier-origin-${s.id}`}><Sparkles size={10} className="inline mr-1"/>AI</Chip>
                        : <span className="chip" style={{ background: "rgba(115,115,115,.14)", color: "var(--text-primary)", borderColor: "rgba(115,115,115,.14)" }} data-testid={`supplier-origin-${s.id}`}>Manuale</span>}
                    </td>
                    <td><StatusChip value={s.status || "Da valutare"}/></td>
                    <td className="text-right whitespace-nowrap">
                      <button onClick={()=>open(s)} data-testid={`supplier-edit-${s.id}`} className="text-[color:var(--text-secondary,#525252)] hover:text-[color:var(--text-primary,#0A0A0A)] mr-3"><Edit3 size={14}/></button>
                      <button onClick={()=>remove(s.id)} className="text-[color:var(--text-secondary,#525252)] hover:text-red-600"><Trash2 size={14}/></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={modal} onClose={()=>setModal(false)} title={editId ? "Modifica fornitore" : "Nuovo fornitore"} wide>
        <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="label-caps block mb-2">Nome <span className="text-[color:var(--accent,#D4AF37)]">*</span></label>
            <input data-testid="supplier-form-name" required className="input-dark" value={form.name||""} onChange={(e)=>setForm({...form, name: e.target.value})}/></div>
          <div><label className="label-caps block mb-2">Referente</label>
            <input data-testid="supplier-form-contact" className="input-dark" value={form.contact||""} onChange={(e)=>setForm({...form, contact: e.target.value})}/></div>
          <div><label className="label-caps block mb-2">Email</label>
            <input data-testid="supplier-form-email" type="email" className="input-dark" value={form.email||""} onChange={(e)=>setForm({...form, email: e.target.value})}/></div>
          <div><label className="label-caps block mb-2">Telefono</label>
            <input data-testid="supplier-form-phone" className="input-dark" value={form.phone||""} onChange={(e)=>setForm({...form, phone: e.target.value})}/></div>
          <div><label className="label-caps block mb-2">Sito web</label>
            <input data-testid="supplier-form-website" placeholder="https://..." className="input-dark" value={form.website||""} onChange={(e)=>setForm({...form, website: e.target.value})}/></div>
          <div><label className="label-caps block mb-2">Città</label>
            <input data-testid="supplier-form-city" className="input-dark" value={form.city||""} onChange={(e)=>setForm({...form, city: e.target.value})}/></div>
          <div><label className="label-caps block mb-2">Paese</label>
            <input data-testid="supplier-form-country" className="input-dark" value={form.country||""} onChange={(e)=>setForm({...form, country: e.target.value})}/></div>
          <div><label className="label-caps block mb-2">Categoria</label>
            <select data-testid="supplier-form-category" className="input-dark" value={form.category||""} onChange={(e)=>setForm({...form, category: e.target.value})}>
              <option value="">—</option>
              {CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select></div>
          <div className="md:col-span-2"><label className="label-caps block mb-2">Prodotti / Servizi</label>
            <input data-testid="supplier-form-products_services" placeholder="es. tessuti lana, cashmere; tinture" className="input-dark" value={form.products_services||""} onChange={(e)=>setForm({...form, products_services: e.target.value})}/></div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="label-caps block mb-2">MOQ</label>
              <input data-testid="supplier-form-moq_value" type="number" step="0.01" min="0" className="input-dark" value={form.moq_value ?? ""} onChange={(e)=>setForm({...form, moq_value: e.target.value})}/></div>
            <div><label className="label-caps block mb-2">Unità</label>
              <input data-testid="supplier-form-moq_unit" placeholder="pz, m, kg…" className="input-dark" value={form.moq_unit||""} onChange={(e)=>setForm({...form, moq_unit: e.target.value})}/></div>
          </div>
          <div><label className="label-caps block mb-2">Lead time (giorni)</label>
            <input data-testid="supplier-form-lead_time_days" type="number" min="0" className="input-dark" value={form.lead_time_days ?? ""} onChange={(e)=>setForm({...form, lead_time_days: e.target.value})}/></div>
          <div><label className="label-caps block mb-2">Stato</label>
            <select data-testid="supplier-form-status" className="input-dark" value={form.status||"Da valutare"} onChange={(e)=>setForm({...form, status: e.target.value})}>
              {STATUSES.map(s => <option key={s}>{s}</option>)}
            </select></div>
          <div className="md:col-span-2"><label className="label-caps block mb-2">Note</label>
            <textarea rows={3} className="input-dark" value={form.notes||""} onChange={(e)=>setForm({...form, notes: e.target.value})}/></div>
          {error && <div className="text-red-600 text-sm md:col-span-2" data-testid="supplier-form-error">{error}</div>}
          <div className="md:col-span-2 flex justify-end gap-3">
            <button type="button" onClick={()=>setModal(false)} className="btn-ghost">Annulla</button>
            <button data-testid="supplier-form-save" className="btn-gold">Salva</button>
          </div>
        </form>
      </Modal>
      {detail && <SupplierDetail supplier={detail} onClose={()=>{ setDetail(null); load(); }}/>}
    </div>
  );
}
function SupplierDetail({ supplier, onClose }) {
  const [refreshing, setRefreshing] = useState(false);
  const [diff, setDiff] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState({}); // field -> bool
  const [applying, setApplying] = useState(false);

  const runRefresh = async () => {
    setError(""); setRefreshing(true); setDiff(null);
    try {
      const { data } = await api.post(`/suppliers/${supplier.id}/refresh`);
      if (!data.found) { setError(data.message || "Nessuna corrispondenza affidabile trovata."); return; }
      setDiff(data);
      const initial = {};
      (data.diff || []).forEach(d => { initial[d.field] = true; });
      setSelected(initial);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setRefreshing(false); }
  };

  const apply = async () => {
    setApplying(true);
    try {
      const fields = {};
      (diff.diff || []).forEach(d => { if (selected[d.field]) fields[d.field] = d.new; });
      const sources = diff.candidate?.sources || [];
      await api.post(`/suppliers/${supplier.id}/apply-refresh`, { fields, sources, verified_at: diff.candidate?.last_verified_at });
      onClose();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setApplying(false); }
  };

  return (
    <Modal open={true} onClose={onClose} title={supplier.name} wide>
      <div data-testid="supplier-detail" className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          {supplier.logo_url && (
            <div className="md:col-span-2 flex items-center gap-4">
              <img src={supplier.logo_url} alt="" className="w-20 h-20 object-contain border border-[color:var(--border,#E5E5E5)] bg-white p-2" onError={(e)=>{e.currentTarget.style.display='none';}}/>
              {supplier.confidence_level && (
                <Chip tone={CONFIDENCE_LABELS[supplier.confidence_level]?.tone || "gold"}>
                  <ShieldCheck size={10} className="inline mr-1"/>{CONFIDENCE_LABELS[supplier.confidence_level]?.label || supplier.confidence_level}
                </Chip>
              )}
              {supplier.italy_verified && <Chip tone="green">Sede Italia verificata</Chip>}
              {supplier.made_in_italy_claim && <Chip tone="gold">Made in Italy dichiarato</Chip>}
            </div>
          )}
          <Field label="Tipo fornitore" value={supplier.supplier_type}/>
          <Field label="Categoria" value={supplier.category}/>
          <Field label="Città" value={supplier.city}/>
          <Field label="Provincia" value={supplier.province}/>
          <Field label="Regione" value={supplier.region}/>
          <Field label="Paese" value={supplier.country}/>
          <Field label="Indirizzo" value={supplier.address}/>
          <Field label="Email" value={supplier.email} icon={<Mail size={12}/>}/>
          <Field label="Telefono" value={supplier.phone} icon={<Phone size={12}/>}/>
          <Field label="MOQ" value={supplier.moq}/>
          <Field label="Tipo produzione" value={supplier.production_type}/>
          <Field label="Certificazioni" value={(supplier.certifications||[]).join(", ")}/>
          {supplier.official_site && (
            <div className="md:col-span-2">
              <div className="label-caps mb-1">Sito ufficiale</div>
              <a href={supplier.official_site} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1 text-sm">
                {supplier.official_site} <ExternalLink size={12}/>
              </a>
            </div>
          )}
          {(supplier.sources || []).length > 0 && (
            <div className="md:col-span-2">
              <div className="label-caps mb-2">Fonti consultate</div>
              <ul className="space-y-1 text-xs">
                {supplier.sources.map((s,i)=>(
                  <li key={i}>
                    <a href={s.url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">
                      {s.title || s.url} <ExternalLink size={10}/>
                    </a>
                    {s.consulted_at && <span className="text-[color:var(--text-muted,#737373)] ml-2">({new Date(s.consulted_at).toLocaleDateString("it-IT")})</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="border-t border-[color:var(--border,#E5E5E5)] pt-4">
          {!diff ? (
            <div className="flex items-center justify-between">
              <div className="text-xs text-[color:var(--text-secondary,#525252)]">
                {supplier.last_verified_at && `Ultima verifica: ${new Date(supplier.last_verified_at).toLocaleDateString("it-IT")}`}
              </div>
              <button data-testid="supplier-refresh-btn" onClick={runRefresh} disabled={refreshing} className="btn-ghost inline-flex items-center gap-2">
                {refreshing ? <Loader2 size={14} className="animate-spin"/> : <RefreshCw size={14}/>}
                Aggiorna dati dal web
              </button>
            </div>
          ) : (
            <div data-testid="supplier-refresh-diff">
              <div className="mb-3 font-medium">Modifiche proposte dal web</div>
              {(diff.diff || []).length === 0 && <div className="text-sm text-[color:var(--text-secondary,#525252)]">Nessun cambiamento rilevato.</div>}
              <div className="space-y-2">
                {(diff.diff || []).map(d => (
                  <label key={d.field} className="flex gap-3 items-start p-3 border border-[color:var(--border,#E5E5E5)]">
                    <input type="checkbox" checked={!!selected[d.field]} onChange={(e)=>setSelected(s=>({...s,[d.field]:e.target.checked}))}/>
                    <div className="flex-1 text-xs">
                      <div className="uppercase tracking-widest text-[color:var(--text-muted,#737373)] mb-1">{d.field}</div>
                      <div className="line-through text-[color:var(--text-secondary,#525252)]">{fmtVal(d.old)}</div>
                      <div className="text-green-700">{fmtVal(d.new)}</div>
                    </div>
                  </label>
                ))}
              </div>
              <div className="mt-3 text-xs text-[color:var(--text-muted,#737373)]">
                Note manuali, tag e stato relazione non vengono mai sovrascritti.
              </div>
              <div className="mt-4 flex gap-3 justify-end">
                <button className="btn-ghost" onClick={()=>{setDiff(null); setSelected({});}}>Annulla</button>
                <button data-testid="supplier-refresh-apply" className="btn-gold inline-flex items-center gap-2" onClick={apply} disabled={applying}>
                  {applying && <Loader2 size={14} className="animate-spin"/>} Applica selezionati
                </button>
              </div>
            </div>
          )}
          {error && <div className="text-red-600 text-sm mt-2">{error}</div>}
        </div>
      </div>
    </Modal>
  );
}

function Field({ label, value, icon }) {
  return (
    <div>
      <div className="label-caps mb-1 inline-flex items-center gap-1">{icon}{label}</div>
      <div className="text-sm">{value ? String(value) : <span className="text-[color:var(--text-muted,#737373)] italic">Non disponibile pubblicamente</span>}</div>
    </div>
  );
}

function fmtVal(v) {
  if (v == null || v === "") return "—";
  if (Array.isArray(v)) return v.join(", ");
  return String(v);
}

// -------------------------------------------------------- AI Search tab

function AiSearchTab() {
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState(null); // current search doc
  const [error, setError] = useState("");
  const [clarify, setClarify] = useState(null); // {questions, answers}
  const pollRef = useRef(null);
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState("relevance");
  const [importing, setImporting] = useState({});

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const startSearch = async (nlQuery, answers) => {
    setError(""); setClarify(null);
    try {
      const { data } = await api.post("/suppliers/ai-search", {
        query: nlQuery,
        clarifying_answers: answers || null,
      });
      setSearch(data);
      pollSearch(data.id);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const pollSearch = (sid) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/suppliers/ai-search/${sid}`);
        setSearch(data);
        if (["done", "error", "needs_clarification"].includes(data.status)) {
          clearInterval(pollRef.current); pollRef.current = null;
          if (data.status === "needs_clarification") {
            setClarify({ questions: data.clarifying_questions || [], answers: {} });
          }
        }
      } catch (e) {
        clearInterval(pollRef.current); pollRef.current = null;
        setError(formatApiErrorDetail(e.response?.data?.detail));
      }
    }, 2500);
  };

  const submit = (e) => {
    e.preventDefault();
    if (!query.trim() || query.trim().length < 3) return;
    startSearch(query.trim());
  };

  const submitClarify = (e) => {
    e.preventDefault();
    startSearch(search.query, clarify.answers);
  };

  const importResult = async (idx) => {
    setImporting(m=>({...m,[idx]:"loading"}));
    try {
      await api.post("/suppliers/import-result", { search_id: search.id, result_index: idx });
      setImporting(m=>({...m,[idx]:"saved"}));
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail);
      setImporting(m=>({...m,[idx]:"error"}));
      alert(msg);
    }
  };

  const running = search && (search.status === "running");
  const results = (search?.results) || [];

  const filtered = useMemo(() => filterAndSort(results, filters, sort), [results, filters, sort]);

  const facets = useMemo(() => buildFacets(results), [results]);

  return (
    <div data-testid="ai-search-tab" className="space-y-6">
      <div className="card-dark p-6">
        <div className="label-caps mb-3 inline-flex items-center gap-2"><Sparkles size={12}/> Trova fornitori con AI</div>
        <p className="text-sm text-[color:var(--text-secondary,#525252)] mb-4">
          Scrivi in linguaggio naturale cosa cerchi. La ricerca è eseguita in tempo reale sul web
          e usa l'AI solo per analizzare i risultati; nessun dato è inventato.
        </p>
        <form onSubmit={submit} className="flex flex-col md:flex-row gap-3">
          <textarea
            data-testid="ai-search-input"
            rows={2}
            className="input-dark flex-1"
            placeholder='Es. "Cerco un produttore italiano di bottoni personalizzati in metallo, possibilmente nel Lazio o in Toscana, con quantitativi minimi bassi."'
            value={query} onChange={(e)=>setQuery(e.target.value)}
            disabled={running}
          />
          <button data-testid="ai-search-submit" type="submit" disabled={running || query.trim().length<3}
            className="btn-gold inline-flex items-center gap-2 h-fit self-start md:self-stretch px-6">
            {running ? <Loader2 size={14} className="animate-spin"/> : <Sparkles size={14}/>}
            Avvia ricerca
          </button>
        </form>
      </div>

      {error && (
        <div data-testid="ai-search-error" className="border border-red-400 bg-red-50 text-red-800 p-4 text-sm">{error}</div>
      )}

      {running && (
        <div data-testid="ai-search-progress" className="card-dark p-6 flex items-center gap-3">
          <Loader2 size={16} className="animate-spin"/>
          <div>
            <div className="text-sm font-medium">Ricerca in corso...</div>
            <div className="text-xs text-[color:var(--text-secondary,#525252)]">{search?.progress || "..."}</div>
          </div>
        </div>
      )}

      {clarify && (
        <form onSubmit={submitClarify} data-testid="ai-search-clarify" className="card-dark p-6 space-y-4">
          <div className="label-caps flex items-center gap-2"><Info size={12}/> Servono 1-2 chiarimenti</div>
          {clarify.questions.map((q, i) => (
            <div key={i}>
              <label className="block text-sm mb-2">{q}</label>
              <input className="input-dark" value={clarify.answers[q] || ""} onChange={(e)=>setClarify(c=>({...c, answers:{...c.answers, [q]: e.target.value}}))}/>
            </div>
          ))}
          <div className="flex justify-end gap-3">
            <button type="button" className="btn-ghost" onClick={()=>setClarify(null)}>Annulla</button>
            <button className="btn-gold" data-testid="ai-search-clarify-submit">Riprova ricerca</button>
          </div>
        </form>
      )}

      {search?.status === "done" && (
        <div className="space-y-4">
          {search.search_summary && (
            <div className="text-sm text-[color:var(--text-secondary,#525252)] italic">{search.search_summary}</div>
          )}
          {results.length === 0 ? (
            <Empty title="Nessun fornitore italiano trovato" hint="Prova a essere più specifico o a cambiare regione/categoria."/>
          ) : (
            <>
              <FiltersBar facets={facets} filters={filters} setFilters={setFilters} sort={sort} setSort={setSort} total={results.length} filtered={filtered.length}/>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="ai-search-results">
                {filtered.map(({ __idx, ...r }) => (
                  <ResultCard key={__idx} idx={__idx} result={r} state={importing[__idx]} onSave={()=>importResult(__idx)}/>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function FiltersBar({ facets, filters, setFilters, sort, setSort, total, filtered }) {
  const set = (k, v) => setFilters(f => ({ ...f, [k]: v || undefined }));
  return (
    <div className="flex flex-wrap gap-2 items-center text-xs">
      <span className="text-[color:var(--text-muted,#737373)]">{filtered}/{total} risultati</span>
      <select data-testid="ai-filter-region" className="input-dark py-1 max-w-[160px]" value={filters.region||""} onChange={(e)=>set("region",e.target.value)}>
        <option value="">Tutte le regioni</option>
        {facets.regions.map(r=><option key={r}>{r}</option>)}
      </select>
      <select className="input-dark py-1 max-w-[160px]" value={filters.province||""} onChange={(e)=>set("province",e.target.value)}>
        <option value="">Tutte le province</option>
        {facets.provinces.map(p=><option key={p}>{p}</option>)}
      </select>
      <select className="input-dark py-1 max-w-[160px]" value={filters.confidence||""} onChange={(e)=>set("confidence",e.target.value)}>
        <option value="">Ogni affidabilità</option>
        {Object.entries(CONFIDENCE_LABELS).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
      </select>
      <select className="input-dark py-1 max-w-[160px]" value={filters.production_type||""} onChange={(e)=>set("production_type",e.target.value)}>
        <option value="">Ogni produzione</option>
        <option value="artigianale">Artigianale</option>
        <option value="industriale">Industriale</option>
      </select>
      <label className="inline-flex items-center gap-1"><input type="checkbox" checked={!!filters.official_only} onChange={(e)=>set("official_only",e.target.checked)}/> Solo sito ufficiale</label>
      <label className="inline-flex items-center gap-1"><input type="checkbox" checked={!!filters.contacts_only} onChange={(e)=>set("contacts_only",e.target.checked)}/> Con contatti</label>
      <div className="ml-auto flex items-center gap-1">
        <span className="text-[color:var(--text-muted,#737373)]">Ordina:</span>
        <select data-testid="ai-sort" className="input-dark py-1" value={sort} onChange={(e)=>setSort(e.target.value)}>
          <option value="relevance">Pertinenza</option>
          <option value="confidence">Affidabilità</option>
          <option value="completeness">Completezza profilo</option>
        </select>
      </div>
    </div>
  );
}

function buildFacets(results) {
  const uniq = (arr) => Array.from(new Set(arr.filter(Boolean))).sort();
  return {
    regions: uniq(results.map(r => r.region)),
    provinces: uniq(results.map(r => r.province)),
  };
}

const CONF_ORDER = { verified_official: 3, multi_source: 2, partial: 1, unverified: 0 };

function filterAndSort(results, filters, sort) {
  const idxed = results.map((r, i) => ({ ...r, __idx: i }));
  let out = idxed.filter(r => {
    if (filters.region && r.region !== filters.region) return false;
    if (filters.province && r.province !== filters.province) return false;
    if (filters.confidence && r.confidence_level !== filters.confidence) return false;
    if (filters.production_type && r.production_type !== filters.production_type) return false;
    if (filters.official_only && !r.official_site) return false;
    if (filters.contacts_only && !(r.email || r.phone)) return false;
    return true;
  });
  if (sort === "confidence") {
    out.sort((a,b) => (CONF_ORDER[b.confidence_level]||0) - (CONF_ORDER[a.confidence_level]||0));
  } else if (sort === "completeness") {
    const score = r => ["official_site","email","phone","city","region","address","moq"].filter(k=>r[k]).length;
    out.sort((a,b) => score(b) - score(a));
  }
  // relevance = keep server order
  return out;
}

function ResultCard({ idx, result, state, onSave }) {
  const conf = CONFIDENCE_LABELS[result.confidence_level] || CONFIDENCE_LABELS.partial;
  return (
    <div data-testid={`ai-result-${idx}`} className="card-dark p-5 flex flex-col gap-3">
      <div className="flex items-start gap-3">
        {result.logo_url ? (
          <img src={result.logo_url} alt="" className="w-14 h-14 object-contain bg-white p-1 border border-[color:var(--border,#E5E5E5)] shrink-0"
               onError={(e)=>{e.currentTarget.style.display='none';}}/>
        ) : <div className="w-14 h-14 shrink-0 bg-[color:var(--surface,#F5F5F5)] border border-[color:var(--border,#E5E5E5)] flex items-center justify-center text-[color:var(--text-muted,#737373)]"><FileText size={20}/></div>}
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">{result.name}</div>
          <div className="text-xs text-[color:var(--text-secondary,#525252)] truncate">{result.supplier_type || "—"}</div>
          <div className="flex flex-wrap gap-1 mt-2">
            <Chip tone={conf.tone}><ShieldCheck size={10} className="inline mr-1"/>{conf.label}</Chip>
            {result.italy_verified ? <Chip tone="green">Italia verificata</Chip> : <Chip tone="red">Italia da verificare</Chip>}
            {result.made_in_italy_claim && <Chip tone="gold">Made in Italy</Chip>}
          </div>
        </div>
      </div>

      <div className="text-xs space-y-1">
        {(result.city || result.region) && (
          <div className="inline-flex items-center gap-1"><MapPin size={11}/>{[result.city, result.province, result.region].filter(Boolean).join(", ")}</div>
        )}
        {result.official_site && (
          <div className="inline-flex items-center gap-1 truncate">
            <ExternalLink size={11}/>
            <a href={result.official_site} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline truncate">{result.official_site}</a>
          </div>
        )}
        {result.email && <div className="inline-flex items-center gap-1"><Mail size={11}/><a href={`mailto:${result.email}`} className="hover:underline">{result.email}</a></div>}
        {result.phone && <div className="inline-flex items-center gap-1"><Phone size={11}/>{result.phone}</div>}
      </div>

      {result.relevance_reason && (
        <div className="text-xs text-[color:var(--text-secondary,#525252)] italic border-l-2 border-[color:var(--border,#E5E5E5)] pl-3">
          {result.relevance_reason}
        </div>
      )}

      <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted,#737373)] flex flex-wrap gap-3">
        <span>Prodotti: {result.products?.length ? result.products.join(", ") : <em>—</em>}</span>
        {result.moq && <span>MOQ: {result.moq}</span>}
        {(result.certifications || []).length > 0 && <span>Certificazioni: {result.certifications.join(", ")}</span>}
      </div>

      {(result.sources || []).length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-[color:var(--text-muted,#737373)]">Fonti consultate ({result.sources.length})</summary>
          <ul className="mt-2 space-y-1">
            {result.sources.map((s,i)=>(
              <li key={i}>
                <a href={s.url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">
                  <ExternalLink size={10}/>{s.title || s.url}
                </a>
              </li>
            ))}
          </ul>
        </details>
      )}

      <div className="mt-auto flex items-center gap-2">
        <button
          data-testid={`ai-save-${idx}`}
          className={`btn-gold inline-flex items-center gap-2 ${state==="saved"?"opacity-60 pointer-events-none":""}`}
          onClick={onSave}
          disabled={state==="loading" || state==="saved"}>
          {state==="loading" ? <Loader2 size={12} className="animate-spin"/> : <Plus size={12}/>}
          {state==="saved" ? "Salvato" : "Salva nei miei fornitori"}
        </button>
        <span className="text-[10px] text-[color:var(--text-muted,#737373)]">Ultima verifica: {result.last_verified_at ? new Date(result.last_verified_at).toLocaleDateString("it-IT") : "—"}</span>
      </div>
    </div>
  );
}
